import { PageWithHeader } from '@backstage/core-components';
import { useContext, useEffect, useState, useRef } from 'react';
import { Button, Theme, makeStyles, createStyles } from '@material-ui/core';
import { useApi } from '@backstage/core-plugin-api';
import { EmeraldSearch } from '@emerald-react/search';
import { EmeraldButtonGroup } from '@emerald-react/button-group';
import {
  EmeraldToast,
  ToastPosition,
  ToastRef,
  ToastType,
} from '@emerald-react/toast';
import { DevxBreadCrumb } from '../common/BreadcrumbsNav/DevxBreadCrumb';
import './styles/GitHubCostCenter.css';
import {
  GitHubCostCenterApi,
  gitHubCostCenterRef,
} from '../../apis/gitHubCostCenterConfigApi';
import { FrontendConfigContext } from '../../providers/FrontendConfigProvider';
// Import types
import {
  Source,
  ViewMode,
  AdvancedFilterKey,
  AdvancedFilters,
  UserTableFilters,
  CostCenterRecord,
  ApiResponse,
  KPIData,
  UsageRecord,
  CostCenterDetails,
} from './interfaces/types';

// Import utilities
import {
  formatPeriod,
  getCurrentMonthValue,
  generateCSV,
  filterCostCenterData,
  getThemeCSSVariables,
  getStaticLogUsageData,
} from './utils/utils';

// Import components
import { KPICards } from './components/kpi-cards/KPICards';
import { LoadingSkeleton } from './components/loading-skeleton/LoadingSkeleton';
import { FilterControls } from './components/filter-controls/FilterControls';
import { CostCenterCard } from './components/cost-center-card/CostCenterCard';
import { CostCenterTable } from './components/cost-center-table/CostCenterTable';
import { CostCenterDetail } from './components/cost-center-detail/CostCenterDetail';
import { BudgetDialog } from './components/budget-dialog/BudgetDialog';
import { MonthYearPicker } from './components/month-year-picker/MonthYearPicker';
import { AuthContext } from '../../providers/AuthProvider';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    gitHubCostCenterThemeScope: getThemeCSSVariables(theme),
  }),
);

const GitHubCostCenter = () => {
  const classes = useStyles();
  const header = {
    title: 'GitHub Copilot Cost Center',
    subTitleRoutes: [
      {
        type: 'link',
        text: 'Home',
        link: '/',
      },
      {
        type: 'text',
        text: 'GitHub Copilot Cost Center',
      },
    ],
  };

  const { profEmail, profileName, isAdmin } = useContext(AuthContext);
  const [breadCrumb] = useState(header);
  const [copilotData, setCopilotData] = useState<ApiResponse | null>(null);
  const [emuData, setEmuData] = useState<ApiResponse | null>(null);
  const [costCenterDetails, setCostCenterDetails] = useState<CostCenterDetails>(
    {} as CostCenterDetails,
  );
  const [loading, setLoading] = useState(true);
  const [selectedSource, setSelectedSource] = useState<string>('');
  const gitHubCostCenterApi: GitHubCostCenterApi = useApi(gitHubCostCenterRef);
  const { frontendConfigData } = useContext(FrontendConfigContext);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [sourceFilter] = useState<Source | 'all'>('all');
  const [selectedPeriod, setSelectedPeriod] = useState(getCurrentMonthValue);
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [highlightedDetailUserKey, setHighlightedDetailUserKey] = useState<
    string | null
  >(null);
  const globalSearchRef = useRef<HTMLDivElement>(null);
  const filtersDropdownRef = useRef<HTMLDivElement>(null);
  const usersFiltersDropdownRef = useRef<HTMLDivElement>(null);
  const [advancedFilters, setAdvancedFilters] = useState<AdvancedFilters>({
    hideNoUsers: false,
    hideNoBudget: false,
    hideNoActivity: false,
    overBudget: false,
    highUtilization: false,
  });
  const [showUsersFilters, setShowUsersFilters] = useState(false);
  const [userTableFilters, setUserTableFilters] = useState<UserTableFilters>({
    noCostCenter: false,
    activeNoCostCenter: false,
    bothSources: false,
    noEmail: false,
    hideNoUsage: false,
  });
  // const [selectedCostCenterData, setSelectedCostCenterData] = useState<
  //   CostCenterRecord[]
  // >([]);
  const [selectedCostCenter, setSelectedCostCenter] =
    useState<CostCenterRecord | null>(null);
  const [usageHistoryPage, setUsageHistoryPage] = useState(0);
  const [usersPage, setUsersPage] = useState(0);
  const [budgetDialogOpen, setBudgetDialogOpen] = useState(false);
  const [budgetInput, setBudgetInput] = useState('');
  const getEmptyKPIData = (): KPIData => ({
    totalCostCenters: 0,
    totalUsers: 0,
    totalSharedPoolAIC: 0,
    totalUtilizedAIC: 0,
    totalActualSpend: 0,
    overusedCostCenters: 0,
    totalExcessSpend: 0,
  });
  const [kpis, setKpis] = useState<KPIData>(getEmptyKPIData());
  const [executeRefresh, setExecuteRefresh] = useState(false);
  const [showLoadingBudget, setShowLoadingBudget] = useState(false);
  const [refreshOnBack, setRefreshOnBack] = useState(false);
  const [isBudgetUpdated, setIsBudgetUpdated] = useState(false);
  const topRightToast = useRef<ToastRef>(null);
  const [originalCostCentersData, setOriginalCostCentersData] = useState<{
    entCostCenters: CostCenterRecord[];
    emuCostCenters: CostCenterRecord[];
  }>({
    entCostCenters: [],
    emuCostCenters: [],
  });

  // Debounce search query
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300); // 300ms delay

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  useEffect(() => {
    // Reset pagination when cost center changes
    setUsageHistoryPage(0);
    setUsersPage(0);
  }, [selectedCostCenter]);

  useEffect(() => {
    updateCostCenterData(
      originalCostCentersData.entCostCenters,
      originalCostCentersData.emuCostCenters,
    );
  }, [selectedSource, debouncedSearchQuery, advancedFilters]);

  useEffect(() => {
    if (costCenterDetails) {
      const currentCostCenterData = getCurrentCostCenterData();
      const currentUsersData = getCurrentUsersData();
      const usersList: string[] = [];
      currentCostCenterData.forEach(costCenter => {
        if (costCenter.resources) {
          costCenter.resources.forEach(user => {
            if (usersList.indexOf(user.email + '::' + user.name) === -1) {
              usersList.push(user.email + '::' + user.name);
            }
          });
        }
      });
      const filteredUsersData = currentUsersData.filter(
        user => usersList.indexOf(user.email + '::' + user.user) !== -1,
      );
      setKpis(calculateKPIs(currentCostCenterData, filteredUsersData));
    }
  }, [costCenterDetails]);

  const onBudgetUpdate = async () => {
    setBudgetDialogOpen(false);
    if (selectedCostCenter) {
      setShowLoadingBudget(true);
      console.log('Updating budget for cost center:', selectedCostCenter);
      gitHubCostCenterApi
        .updateBudget(
          selectedCostCenter?.source == 'CBRE-EMU' ? 'cbre-emu' : 'cbre',
          selectedCostCenter?.budgetId || '',
          profEmail ||
            selectedCostCenter?.subscriptionData?.tags?.BillingApprover ||
            '',
          parseFloat(budgetInput),
        )
        .then(resolve => {
          console.log('Budget update result:', resolve);
          gitHubCostCenterApi
            .updateDesignMetrics(
              getStaticLogUsageData(
                profEmail || '',
                profileName || '',
                selectedCostCenter?.budgetAmount || 0,
                parseFloat(budgetInput),
                selectedCostCenter?.costCenterId || '',
                selectedCostCenter?.subscriptionData?.tags?.BillingApprover,
                selectedCostCenter?.budgetId,
              ),
              frontendConfigData?.figma?.token || '',
            )
            .then(res => console.log(res))
            .catch(err => console.log(err));

          setShowLoadingBudget(false);
          topRightToast.current?.show({
            type: ToastType.SUCCESS,
            title: 'Budget updated successfully.',
            className: 'custom-toast-github-cost-center-success',
            message:
              "Your budget has been updated. You're all set to keep working, the new values will reflect in the UI during tomorrow's scheduled refresh.", //resolve.message,
            autoCloseDelay: 15000,
          });
          // setExecuteRefresh(true);
          // setIsBudgetUpdated(true);
        })
        .catch(error => {
          console.log('error', error);
          setShowLoadingBudget(false);
          topRightToast.current?.show({
            type: ToastType.ERROR,
            title: 'Error',
            autoCloseDelay: 15000,
            message:
              //error?.response?.data?.message ||
              // error.message ||
              'Failed to update budget. Please try again later.',
          });
        });
    }
  };

  const displayPeriod = selectedPeriod || getCurrentMonthValue();
  const activeAdvancedFilterCount =
    Object.values(advancedFilters).filter(Boolean).length;

  const toggleAdvancedFilter = (filterKey: AdvancedFilterKey) => {
    setAdvancedFilters(currentFilters => ({
      ...currentFilters,
      [filterKey]: !currentFilters[filterKey],
    }));
  };

  const toggleUserTableFilter = (filterKey: keyof UserTableFilters) => {
    setUserTableFilters(currentFilters => ({
      ...currentFilters,
      [filterKey]: !currentFilters[filterKey],
    }));
  };

  const activeUserFilterCount =
    Object.values(userTableFilters).filter(Boolean).length;

  useEffect(() => {
    if (!highlightedDetailUserKey) {
      return;
    }

    const timeout = window.setTimeout(() => {
      setHighlightedDetailUserKey(currentKey =>
        currentKey === highlightedDetailUserKey ? null : currentKey,
      );
    }, 3500);

    return () => {
      window.clearTimeout(timeout);
    };
  }, [highlightedDetailUserKey]);

  useEffect(() => {
    if (frontendConfigData && gitHubCostCenterApi) {
      getUserList();
    }
  }, [frontendConfigData, gitHubCostCenterApi, selectedPeriod]);

  useEffect(() => {
    if (refreshOnBack) {
      setRefreshOnBack(false);
      getUserList();
    }
  }, [refreshOnBack]);

  const getUserList = async () => {
    try {
      setLoading(true);
      const period = selectedPeriod.split('-');
      const allResults = await Promise.allSettled([
        gitHubCostCenterApi.getCostCenters(
          'cbre-copilot',
          'any',
          period[0],
          period[1],
        ),
        gitHubCostCenterApi.getCostCenters(
          'cbre-emu',
          'any',
          period[0],
          period[1],
        ),
        gitHubCostCenterApi.getUsersList(
          'cbre-copilot',
          'any',
          period[0],
          period[1],
        ),
        gitHubCostCenterApi.getUsersList(
          'cbre-emu',
          'any',
          period[0],
          period[1],
        ),
      ]);
      if (allResults[2].status === 'fulfilled') {
        setCopilotData(allResults[2].value);
      }
      if (allResults[3].status === 'fulfilled') {
        setEmuData(allResults[3].value);
      }
      const entCostCenters =
        allResults[0].status === 'fulfilled'
          ? allResults[0].value?.data || []
          : [];
      const emuCostCenters =
        allResults[1].status === 'fulfilled'
          ? allResults[1].value?.data || []
          : [];
      setOriginalCostCentersData({
        entCostCenters,
        emuCostCenters,
      });
      updateCostCenterData(entCostCenters, emuCostCenters);
      setSelectedSource('all');
    } catch (error) {
      console.log('error', error);
    } finally {
      setLoading(false);
    }
  };

  const exportOverviewCSV = () => {
    const data = getCurrentCostCenterData();
    const headers = [
      'Name',
      'Source',
      'State',
      'Users',
      'Period',
      'Budget ($)',
      'Consumed ($)',
      'Utilization (%)',
    ];
    const rows = data.map(cc => {
      const budgetAmount = cc.budgetAmount || 0;
      const consumedAmount = cc.consumedAmount || 0;
      const utilization =
        budgetAmount > 0
          ? Math.min((consumedAmount / budgetAmount) * 100, 100)
          : 0;
      return [
        cc.name,
        cc.source,
        cc.state,
        String(cc.resources?.length || 0),
        formatPeriod(displayPeriod),
        String(budgetAmount),
        String(consumedAmount),
        utilization.toFixed(1),
      ];
    });
    generateCSV(headers, rows, `cost-centers-${displayPeriod}.csv`);
  };

  const exportUsageHistoryCSV = () => {
    if (!selectedCostCenter) return;
    const headers = [
      'Period',
      'State',
      'Number of Users',
      'Budget Amount ($)',
      'Consumed Amount ($)',
    ];
    const rows = [
      ['Jul 2026', 'Active', '40', '18720', '13150'],
      ['Jun 2026', 'Active', '41', '18180', '12770'],
      ['May 2026', 'Active', '39', '17460', '12260'],
    ];
    generateCSV(headers, rows, `${selectedCostCenter.name}-usage-history.csv`);
  };

  const exportUsersCSV = () => {
    if (!selectedCostCenter) return;
    const headers = [
      'Username',
      'Email',
      'Excess Spend ($)',
      'Actual Spend ($)',
    ];
    const rows =
      selectedCostCenter.resources?.map(user => [
        user.name,
        user.email,
        '0.00',
        '0.00',
      ]) || [];
    generateCSV(
      headers,
      rows,
      `${selectedCostCenter.name}-users-${displayPeriod}.csv`,
    );
  };

  const applyRequiredFilters = (data: CostCenterRecord[]) => {
    // Apply search query filter
    if (debouncedSearchQuery.trim()) {
      const lowerSearchQuery = debouncedSearchQuery.toLowerCase();
      data = data.filter(cc => {
        const costCennterNameMatch = cc.name
          .toLowerCase()
          .includes(lowerSearchQuery);
        const userNameOrEmailMatch = cc.resources?.some(
          user =>
            user.name.toLowerCase().includes(lowerSearchQuery) ||
            user.email.toLowerCase().includes(lowerSearchQuery),
        );
        return costCennterNameMatch || userNameOrEmailMatch;
      });
    }
    // Apply advanced filters
    if (advancedFilters.hideNoUsers) {
      data = data.filter(cc => (cc.resources?.length || 0) > 0);
    }

    if (advancedFilters.hideNoBudget) {
      data = data.filter(
        cc => cc.budgetAmount !== null && cc.budgetAmount !== undefined,
      );
    }

    if (advancedFilters.hideNoActivity) {
      data = data.filter(cc => (cc.consumedAmount || 0) > 0);
    }

    if (advancedFilters.overBudget) {
      data = data.filter(
        cc => (cc.consumedAmount || 0) > (cc.budgetAmount || 0),
      );
    }

    if (advancedFilters.highUtilization) {
      data = data.filter(cc => {
        const budgetAmount = cc.budgetAmount || 0;
        if (budgetAmount === 0) return false;
        const utilization = ((cc.consumedAmount || 0) / budgetAmount) * 100;
        return utilization >= 90;
      });
    }
    return data;
  };

  const updateCostCenterData = (
    originalCBRE_ENTCostCenters: CostCenterRecord[] = [],
    originalCBRE_EMUCostCenters: CostCenterRecord[] = [],
  ) => {
    // if (
    //   (!costCenterDetails || Object.keys(costCenterDetails).length === 0) &&
    //   !originalCBRE_ENTCostCenters.length &&
    //   !originalCBRE_EMUCostCenters.length
    // ) {
    //   return;
    // }

    // if (
    //   originalCBRE_ENTCostCenters.length &&
    //   originalCBRE_EMUCostCenters.length
    // ) {
    const activeUniqueCBRE_ENTCostCenters = filterCostCenterData(
      originalCBRE_ENTCostCenters,
    ) as CostCenterRecord[];
    const activeUniqueCBRE_EMUCostCenters = filterCostCenterData(
      originalCBRE_EMUCostCenters,
    ) as CostCenterRecord[];
    const tempCostCenterDetails = { ...costCenterDetails };
    tempCostCenterDetails.activeUniqueAllCostCenters =
      activeUniqueCBRE_ENTCostCenters.concat(activeUniqueCBRE_EMUCostCenters);
    tempCostCenterDetails.activeUniqueCBRE_ENTCostCenters =
      activeUniqueCBRE_ENTCostCenters;
    tempCostCenterDetails.activeUniqueCBRE_EMUCostCenters =
      activeUniqueCBRE_EMUCostCenters;

    tempCostCenterDetails.originalAllCostCenters =
      originalCBRE_ENTCostCenters.concat(originalCBRE_EMUCostCenters);
    tempCostCenterDetails.originalCBRE_ENTCostCenters =
      originalCBRE_ENTCostCenters;
    tempCostCenterDetails.originalCBRE_EMUCostCenters =
      originalCBRE_EMUCostCenters;
    //}

    tempCostCenterDetails.filteredCBRE_EMUCostCenters = applyRequiredFilters(
      tempCostCenterDetails.activeUniqueCBRE_EMUCostCenters,
    );
    tempCostCenterDetails.filteredCBRE_ENTCostCenters = applyRequiredFilters(
      tempCostCenterDetails.activeUniqueCBRE_ENTCostCenters,
    );
    tempCostCenterDetails.filteredAllCostCenters =
      tempCostCenterDetails.filteredCBRE_EMUCostCenters.concat(
        tempCostCenterDetails.filteredCBRE_ENTCostCenters,
      );
    setCostCenterDetails({ ...tempCostCenterDetails });
  };

  const getCurrentCostCenterData = (): CostCenterRecord[] => {
    return (
      (selectedSource === 'CBRE-EMU'
        ? costCenterDetails.filteredCBRE_EMUCostCenters
        : selectedSource === 'CBRE-ENT'
        ? costCenterDetails.filteredCBRE_ENTCostCenters
        : costCenterDetails.filteredAllCostCenters) || []
    );
  };
  const getCurrentUsersData = (): UsageRecord[] => {
    return selectedSource === 'CBRE-EMU'
      ? emuData?.data || []
      : selectedSource === 'CBRE-ENT'
      ? copilotData?.data || []
      : (emuData?.data || []).concat(copilotData?.data || []);
  };

  const calculateKPIs = (
    data: CostCenterRecord[],
    usersData: UsageRecord[],
  ): KPIData => {
    const kpiData: KPIData = getEmptyKPIData();
    kpiData.totalCostCenters = data.length;
    kpiData.totalUsers = usersData.length;
    kpiData.totalSharedPoolAIC = usersData.length * 3000;
    usersData.forEach(user => {
      user.usageItems?.forEach(item => {
        kpiData.totalUtilizedAIC += item.grossQuantity || 0;
        kpiData.totalActualSpend += item.grossAmount || 0;
        kpiData.totalExcessSpend += item.netAmount || 0;
        kpiData.overusedCostCenters += item.netQuantity || 0;
      });
    });
    return kpiData;
  };

  const renderDashboard = () => {
    if (loading) {
      return <LoadingSkeleton />;
    }

    return (
      <div
        className={`${classes.gitHubCostCenterThemeScope} page-container guide-page-content`}
      >
        <div className="guide-page-wrapper">
          <main className="guide-main-content">
            {costCenterDetails?.originalAllCostCenters && (
              <div className="cost-centers-page">
                <section className="cost-centers-toolbar">
                  <div className="cost-centers-overview-header">
                    <div className="cost-centers-title-block">
                      <h2>GitHub Copilot Cost Center</h2>
                      <span>
                        <i>
                          Note: Monthly Co-Pilot license fees are excluded from
                          totals shown on this dashboard. Please note that the
                          monthly Co-Pilot license fees are not included in the
                          budget that is set for your cost center and can cause
                          your monthly Azure bill to exceed the allocated
                          budget.
                        </i>
                      </span>
                    </div>
                    <div
                      className="cost-centers-search-shell"
                      ref={globalSearchRef}
                    >
                      <EmeraldSearch
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        onClear={() => setSearchQuery('')}
                        placeholder="Search cost centers, users by name or email..."
                        aria-label="Search cost centers"
                        className="cost-centers-search-field-custom"
                      />
                    </div>
                  </div>

                  <KPICards kpis={kpis} />

                  <div className="cost-centers-toolbar-top">
                    <div className="cost-centers-filters">
                      <div className="cost-centers-filter-field">
                        <div
                          className="cost-centers-chip-group"
                          role="group"
                          aria-label="Filter cost centers"
                        >
                          <button
                            type="button"
                            className={`cost-centers-chip cost-centers-chip--all ${
                              sourceFilter === 'all' ? 'is-active' : ''
                            }`}
                            onClick={() => setSelectedSource('all')}
                          >
                            All (
                            {costCenterDetails?.filteredAllCostCenters.length ||
                              0}
                            )
                          </button>
                          <button
                            type="button"
                            className={`cost-centers-chip cost-centers-chip--emu ${
                              sourceFilter === 'CBRE-EMU' ? 'is-active' : ''
                            }`}
                            onClick={() => setSelectedSource('CBRE-EMU')}
                          >
                            CBRE-EMU (
                            {costCenterDetails?.filteredCBRE_EMUCostCenters
                              .length || 0}
                            )
                          </button>
                          <button
                            type="button"
                            className={`cost-centers-chip cost-centers-chip--ent ${
                              sourceFilter === 'CBRE-ENT' ? 'is-active' : ''
                            }`}
                            onClick={() => setSelectedSource('CBRE-ENT')}
                          >
                            CBRE-ENT (
                            {costCenterDetails?.filteredCBRE_ENTCostCenters
                              .length || 0}
                            )
                          </button>
                        </div>
                      </div>
                      <MonthYearPicker
                        value={selectedPeriod}
                        onChange={setSelectedPeriod}
                        minDate="2026-06"
                      />
                    </div>

                    <div className="cost-centers-toolbar-actions">
                      <FilterControls
                        showAdvancedFilters={showAdvancedFilters}
                        advancedFilters={advancedFilters}
                        activeAdvancedFilterCount={activeAdvancedFilterCount}
                        filtersDropdownRef={filtersDropdownRef}
                        onToggleShowFilters={() =>
                          setShowAdvancedFilters(currentValue => !currentValue)
                        }
                        onToggleAdvancedFilter={toggleAdvancedFilter}
                        onClearAll={() => {
                          setAdvancedFilters({
                            hideNoUsers: false,
                            hideNoBudget: false,
                            hideNoActivity: false,
                            overBudget: false,
                            highUtilization: false,
                          });
                          setSearchQuery('');
                        }}
                      />
                      <EmeraldButtonGroup
                        activeButtonIndexes={[
                          ['grid', 'list'].indexOf(viewMode),
                        ]}
                        data={[
                          {
                            label: 'Grid',
                          },
                          {
                            label: 'List',
                          },
                        ]}
                        onChange={(activeIndexs: number[]) =>
                          setViewMode(
                            ['grid', 'list'][activeIndexs[0]] as ViewMode,
                          )
                        }
                      />
                      {/* <ToggleButtonGroup
                          color="primary"
                          value={viewMode}
                          exclusive
                          onChange={(_e, newView) => {
                            if (newView !== null) {
                              setViewMode(newView);
                            }
                          }}
                          aria-label="Switch between grid and list view"
                          className={classes.toggleButtonGroup}
                        >
                          <ToggleButton
                            value="grid"
                            className={classes.toggleButton}
                          >
                            Grid
                          </ToggleButton>
                          <ToggleButton
                            value="list"
                            className={classes.toggleButton}
                          >
                            List
                          </ToggleButton>
                        </ToggleButtonGroup> */}
                      {viewMode === 'list' && (
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          onClick={exportOverviewCSV}
                          style={{ marginLeft: '8px' }}
                        >
                          Export CSV
                        </Button>
                      )}
                    </div>
                  </div>
                </section>

                <section className="cost-centers-results">
                  {viewMode === 'list' ? (
                    <CostCenterTable
                      costCenters={getCurrentCostCenterData()}
                      displayPeriod={displayPeriod}
                      onRowClick={setSelectedCostCenter}
                    />
                  ) : (
                    <div className="cost-center-grid">
                      {getCurrentCostCenterData().map(
                        (costCenter, index, allCards) => (
                          <CostCenterCard
                            key={costCenter.azureSubscription + index}
                            costCenter={costCenter}
                            displayPeriod={displayPeriod}
                            allCards={allCards}
                            onClick={() => setSelectedCostCenter(costCenter)}
                          />
                        ),
                      )}
                    </div>
                  )}
                </section>
              </div>
            )}
          </main>
        </div>
      </div>
    );
  };

  return (
    <PageWithHeader
      title={undefined}
      themeId="promptStore"
      pageTitleOverride={breadCrumb?.title}
      subtitle={<DevxBreadCrumb routes={breadCrumb?.subTitleRoutes} />}
    >
      {selectedCostCenter ? (
        <CostCenterDetail
          selectedCostCenter={selectedCostCenter}
          selectedCostCenterData={getCurrentCostCenterData()}
          displayPeriod={displayPeriod}
          usageHistoryPage={usageHistoryPage}
          usersPage={usersPage}
          showUsersFilters={showUsersFilters}
          userTableFilters={userTableFilters}
          activeUserFilterCount={activeUserFilterCount}
          usersFiltersDropdownRef={usersFiltersDropdownRef}
          executeRefresh={executeRefresh}
          showLoadingBudget={showLoadingBudget}
          onRefreshCostCenterData={() => setExecuteRefresh(false)}
          onBack={() => {
            setSelectedCostCenter(null);
            setRefreshOnBack(isBudgetUpdated);
            setIsBudgetUpdated(false);
          }}
          onEditBudget={() => {
            setBudgetInput(selectedCostCenter.budgetAmount?.toString() || '');
            setBudgetDialogOpen(true);
          }}
          onUsageHistoryPageChange={setUsageHistoryPage}
          onUsersPageChange={setUsersPage}
          onToggleShowUsersFilters={() =>
            setShowUsersFilters(!showUsersFilters)
          }
          onToggleUserTableFilter={toggleUserTableFilter}
          onClearUserFilters={() => {
            setUserTableFilters({
              noCostCenter: false,
              activeNoCostCenter: false,
              bothSources: false,
              noEmail: false,
              hideNoUsage: false,
            });
          }}
          onExportUsageHistoryCSV={exportUsageHistoryCSV}
          onExportUsersCSV={exportUsersCSV}
        />
      ) : (
        renderDashboard()
      )}

      <BudgetDialog
        open={budgetDialogOpen}
        budgetInput={budgetInput}
        selectedCostCenter={selectedCostCenter}
        onClose={() => setBudgetDialogOpen(false)}
        onBudgetInputChange={setBudgetInput}
        onSave={() => onBudgetUpdate()}
      />

      <EmeraldToast ref={topRightToast} position={ToastPosition.TOP_RIGHT} />
    </PageWithHeader>
  );
};

export default GitHubCostCenter;
